import type { ShortEvent } from '@/src/api/eventsApi';
import { formatSlotSummary } from '@/src/utils/dateFormatter';
import { Image } from 'expo-image';
import { Text, View, YStack } from 'tamagui';
import MaybeGrayscale from './ui/MaybeGrayscale';
import PSBadge from './ui/PSBadge';

/**
 * EventCard — the large event tile for the home carousels (molecule).
 *
 * A fixed 3:4 image with an info panel *below* it (no overlap) at the same
 * width.
 *
 * `panel` picks the info-panel fill and is limited to exactly two values —
 * `$backgroundSecondary` (the neutral recessed default) or `$brand` (the purple
 * panel used inside the dark Access Pass block). Text/meta colours derive from
 * it, so the card can't end up with white text on a grey panel. The card stays
 * render-only: it's told which colour to wear, not *why* — the screen decides
 * that (e.g. `panel="$brand"` for Access Pass events).
 *
 * `soldOut` sends the image monotone, dims the whole card, and overlays a
 * `outline`/`large` "Sold out" `PSBadge` at the image's bottom-left; it forces
 * the neutral text treatment regardless of `panel` (a sold-out card is never
 * shown as a highlight). The badge lives *inside* the image's rounded/overflow-hidden box
 * so it clips to the corners; it's absolutely positioned there (its only job is
 * to float over the image, so it doesn't need to reserve flow space).
 *
 * `width` sizes the whole card. Defaults to 240 for the horizontal, snapping
 * carousels; the 2-column grid passes its measured column width instead. Height
 * always follows the 3:4 portrait ratio. Width/height read Tamagui's `size`
 * token group which we don't populate, and raw layout numbers aren't design
 * values the linter guards, so a plain number is correct here.
 *
 * When a card is stretched taller than its content (a carousel/grid row aligns
 * to its tallest card), title/meta stay top-aligned and the slack falls as
 * empty panel *below* the meta. (We compared this against pinning the meta to
 * the panel floor; top-aligned won.)
 */
const DEFAULT_WIDTH = 240;
// Portrait 3:4 (width:height) → height is 4/3 of the width. Matches ImageCarousel.
const RATIO = 4 / 3;

// The only two info-panel fills the card accepts. Union'd so TS rejects any
// other token — you can't dress the panel in an arbitrary colour.
type PanelColor = '$panelTranslucent' | '$backgroundSecondary';

export default function EventCard({
  event,
  width = DEFAULT_WIDTH,
  panelBgColor = '$backgroundSecondary',
  textColor = 'dark',
  onPress,
}: {
  event: ShortEvent;
  width?: number;
  panelBgColor?: PanelColor;
  textColor?: 'dark' | 'light';
  onPress?: () => void;
}) {
  // A sold-out card is never a highlight, so it forces the neutral treatment.

  const titleColor = textColor === 'dark' ? '$textDark' : '$textWhite';
  const metaColor = textColor === 'dark' ? '$textDarkMuted' : '$textWhiteMuted';

  const imgH = width * RATIO;
  const imageUrl = event.images.at(0)?.url;

  const shouldbeGrayScale = event.isInPast || event.isSoldOut;

  return (
    <YStack
      width={width}
      // Deliberately does NOT grow. The card's height comes from the parent row's
      // items="stretch" (the CROSS axis), so flexGrow buys nothing vertically —
      // and on the grid's WRAPPING row it's actively wrong: a lone card on the
      // last line would take the whole row width while its fixed-width image
      // stayed put, drawing a half-width poster over a full-width panel.
      //
      // flexShrink + flexBasis="auto" stay: the basis is `width` above, and the
      // shrink absorbs sub-pixel overflow when a measured column width doesn't
      // halve exactly — without it the second card in a row wraps to its own.
      flexShrink={1}
      flexBasis="auto"
      cursor={onPress ? 'pointer' : undefined}
      onPress={onPress}
    >
      <View
        width={width}
        height={imgH}
        borderTopLeftRadius="$base"
        borderTopRightRadius="$base"
        overflow="hidden"
      >
        <MaybeGrayscale enabled={shouldbeGrayScale}>
          <Image
            source={{ uri: imageUrl }}
            style={{
              width,
              height: imgH,
              opacity: shouldbeGrayScale ? 0.6 : 1,
            }}
            contentFit="cover"
            transition={200}
          />
        </MaybeGrayscale>
        {event.isSoldOut && (
          // Float the badge over the image's bottom-left; the parent's
          // overflow="hidden" clips it to the rounded corners.
          <View position="absolute" bottom="$sm" left="$sm">
            <PSBadge label="Sold out" variant="outline" size="large" />
          </View>
        )}
        {event.isInPast && (
          // Float the badge over the image's bottom-left; the parent's
          // overflow="hidden" clips it to the rounded corners.
          <View position="absolute" bottom="$sm" left="$sm">
            <PSBadge label="In past" variant="outline" size="large" />
          </View>
        )}
      </View>

      <YStack
        // Grow to fill the card's height when a row stretches it taller than the
        // text, so the panel background extends to the bottom rather than hugging
        // the title/meta. The image above is fixed-height, so only this grows.
        flexGrow={1}
        backgroundColor={panelBgColor}
        opacity={shouldbeGrayScale ? 0.6 : 1}
        borderBottomLeftRadius="$base"
        borderBottomRightRadius="$base"
        p="$md"
        gap="$xs"
      >
        <Text
          fontFamily="$body"
          fontSize="$base"
          fontWeight="$semibold"
          numberOfLines={3}
          color={titleColor}
        >
          {event.title}
        </Text>
        <Text fontFamily="$body" fontSize="$base" color={metaColor}>
          {event.nextBookableSlot
            ? formatSlotSummary(event.nextBookableSlot, event.lastBookableSlot)
            : 'Already over'}
        </Text>
        <Text
          fontFamily="$body"
          fontSize="$base"
          numberOfLines={1}
          color={metaColor}
        >
          @ {event.venue.name}
        </Text>
      </YStack>
    </YStack>
  );
}
